import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Select, Empty, Drawer, Table } from 'antd';
import * as echarts from 'echarts';
import { Col, Row } from 'reactstrap';
import UserLabel from '../../../utils/UserLabel';
import { colors } from '../../../utils/Utils';
import { render } from '@testing-library/react';

const MaterialWiseSavings: React.FC<any> = ({ materialWiseSavingsData }) => {
    console.log("materialWiseSavingsData", materialWiseSavingsData);
    const supplierData = materialWiseSavingsData?.supplierData || [];
    const materialWiseAvg = materialWiseSavingsData?.materialWiseAvg;
    const materials = materialWiseSavingsData?.materialWiseSavings;
    const [visible, setVisible] = useState(false);
    const flattenedArray = useMemo(() => {
        return materials ? Object.values(materials).flat() : [];
    }, [materials]); // Only recalculate when materials change

    const [currentData, setCurrentData] = useState<any>([]);
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState(0);

    useEffect(() => {
        const updateWidth = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth);
            }
        };
        updateWidth();
        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
    }, []);

    useEffect(() => {
        console.log("flattenedArray", flattenedArray);
        setCurrentData(flattenedArray);
    }, [flattenedArray]);

    useEffect(() => {
        if (currentData.length > 0) {
            const chartDom = document.getElementById('material-savings-chart') as HTMLElement;
            if (chartDom) {
                const myChart = echarts.init(chartDom);

                const option = {
                    title: {
                        text: `Avg. Savings : ${materialWiseAvg} %`,
                    },
                    tooltip: {
                        trigger: 'axis',
                        axisPointer: { type: 'shadow' },
                    },
                    grid: {
                        left: 80,
                        right: 30,
                        bottom: 100,
                        containLabel: false,
                    },
                    xAxis: {
                        type: 'category',
                        data: currentData.map((item: any) => item.material),
                        name: 'Material',
                        nameLocation: 'middle',
                        nameTextStyle: {
                            fontWeight: 'bold',
                            fontSize: 14,
                            padding: 60,
                        },
                        axisLabel: {
                            interval: 0,
                            rotate: 30,
                            formatter: (value: string) => {
                                return value.length > 15 ? value.substring(0, 15) + '...' : value;
                            },
                            fontSize: 10,
                        },
                    },
                    yAxis: {
                        type: 'value',
                        axisLabel: { formatter: '{value}%' },
                        name: 'Avg. Savings (%)',
                        nameLocation: 'middle',
                        nameTextStyle: {
                            fontWeight: 'bold',
                            fontSize: 14,
                            padding: 40,
                        },
                    },
                    series: [
                        {
                            name: 'Savings',
                            type: 'bar',
                            data: currentData.map((item: any) => item.savings),
                            itemStyle: {
                                color: '#6675FF',
                            },
                            barWidth: '20%',
                            barCategoryGap: '100%',
                        },
                    ],
                };

                myChart.setOption(option);

                return () => {
                    myChart.dispose();
                };
            }
        }
    }, [currentData, materialWiseAvg]);

    const openDrawer = () => {
        console.log("currentData", materialWiseSavingsData.supplierData);
        setVisible(true);
    }

    const supplierColorMap = useMemo(() => {
        const map: { [key: string]: string } = {};
        let colorIndex = 0;

        supplierData.forEach((record: any) => {
            if (!map[record.supplierName]) {
                map[record.supplierName] = colors[colorIndex % colors.length];
                colorIndex++;
            }
        });

        return map;
    }, [supplierData]);

    const columns = [
        {
            title: "Supplier Name",
            dataIndex: "supplierName",
            key: "supplierName",
            render: (text: string, record: any) => (
                <UserLabel
                    supplierName={record.supplierName}
                    color={supplierColorMap[record.supplierName]}
                    text={text}
                />
            ),
        },
        {
            title: "Savings",
            dataIndex: "savings",
            key: "savings",
            render: (text: any) => {
                const numberText = Number(text); // Convert text to a number
                return !isNaN(numberText) ? `${numberText.toFixed(2)}%` : 'N/A'; // Check if it's a valid number
            },
        }
    ];


    const onClose = () => {
        setVisible(false);
    }

    return (
        <>
            <div ref={containerRef} style={{ minWidth: 0 }}>
                <div className="card-title-group mb-1" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div className="card-title">
                        <h6 className="title">Material Wise Savings</h6>
                        <p>Insights on savings achieved across different materials.</p>
                    </div>
                    {/* {supplierData.length} */}
                    {supplierData.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <a className="title" style={{ cursor: 'pointer' }}
                                onClick={() => {
                                    openDrawer()
                                }}
                            >View supplier wise savings</a>
                        </div>
                    )}

                </div>
                {currentData.length > 0 ? (
                    <div style={{ maxWidth: containerWidth > 0 ? `${containerWidth}px` : '100%', overflowX: currentData.length > 20 ? 'auto' : 'hidden' }}>
                        <div id="material-savings-chart" style={{ width: currentData.length > 20 ? `${currentData.length * 50}px` : '100%', height: '400px' }} />
                    </div>
                ) : (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                        <Empty description="No Data found for the selected category" />
                    </div>
                )}
            </div>

            <Drawer
                title={"Supplier savings"}
                width={"35vw"}
                onClose={onClose}
                open={visible}
                bodyStyle={{ paddingBottom: 80 }}
            >

                <Table
                    columns={columns}
                    dataSource={supplierData}
                    rowKey="id"
                    size="small"
                    pagination={false}
                />
            </Drawer>

        </>
    );
};

export default MaterialWiseSavings;
